import { create } from 'zustand'
import type { LintFinding } from '../api/types'
import { DEFAULT_SORT, type SortKey } from '../lib/sort'
import type { StatusKind } from '../lib/status'

export type CellKey = string

export const cellKey = (keyId: string, locale: string, pluralForm: string): CellKey =>
  `${keyId}|${locale}|${pluralForm}`

export interface Toast {
  id: number
  kind: 'error' | 'info' | 'success'
  text: string
}

export interface Cursor {
  row: number
  col: number
}

interface UiState {
  // filters
  search: string
  tag: string | null
  status: StatusKind | null
  showDeprecated: boolean
  setSearch: (value: string) => void
  setTag: (tag: string | null) => void
  setStatus: (status: StatusKind | null) => void
  setShowDeprecated: (value: boolean) => void
  // A sort order, not a filter — resetFilters leaves it alone, same as
  // showDeprecated and hiddenLocales.
  sort: SortKey
  setSort: (sort: SortKey) => void
  resetFilters: () => void

  // locale columns, per platform
  hiddenLocales: Record<string, string[]>
  toggleLocale: (platform: string, code: string) => void

  // grid cursor + editing
  cursor: Cursor
  setCursor: (cursor: Cursor) => void
  editing: CellKey | null
  setEditing: (key: CellKey | null) => void

  // row expansion (plurals + context note)
  expanded: Record<string, boolean>
  toggleExpanded: (keyId: string) => void
  clearExpanded: () => void

  // bulk selection
  selected: Record<string, boolean>
  toggleSelected: (keyId: string) => void
  selectMany: (keyIds: string[], value: boolean) => void
  clearSelection: () => void

  // lint findings from the last save of a cell
  lint: Record<CellKey, LintFinding[]>
  setLint: (key: CellKey, findings: LintFinding[]) => void

  // history drawer
  historyFor: { translationId: string; keyName: string; locale: string } | null
  openHistory: (value: UiState['historyFor']) => void

  // toasts
  toasts: Toast[]
  pushToast: (kind: Toast['kind'], text: string) => void
  dismissToast: (id: number) => void
}

let toastSeq = 0

export const useUi = create<UiState>((set) => ({
  search: '',
  tag: null,
  status: null,
  // Not cleared by resetFilters: this is a view mode (findable/restorable),
  // like hiddenLocales, not a transient search/tag/status filter.
  showDeprecated: false,
  setSearch: (search) => set({ search }),
  setTag: (tag) => set({ tag }),
  setStatus: (status) => set({ status }),
  setShowDeprecated: (showDeprecated) => set({ showDeprecated }),
  sort: DEFAULT_SORT,
  setSort: (sort) => set({ sort }),
  resetFilters: () => set({ search: '', tag: null, status: null }),

  hiddenLocales: {},
  toggleLocale: (platform, code) =>
    set((state) => {
      const current = state.hiddenLocales[platform] ?? []
      const next = current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code]
      return { hiddenLocales: { ...state.hiddenLocales, [platform]: next } }
    }),

  cursor: { row: 0, col: 0 },
  setCursor: (cursor) => set({ cursor }),
  editing: null,
  setEditing: (editing) => set({ editing }),

  expanded: {},
  toggleExpanded: (keyId) =>
    set((state) => ({ expanded: { ...state.expanded, [keyId]: !state.expanded[keyId] } })),
  clearExpanded: () => set({ expanded: {} }),

  selected: {},
  toggleSelected: (keyId) =>
    set((state) => {
      const next = { ...state.selected }
      if (next[keyId]) delete next[keyId]
      else next[keyId] = true
      return { selected: next }
    }),
  selectMany: (keyIds, value) =>
    set((state) => {
      const next = { ...state.selected }
      for (const id of keyIds) {
        if (value) next[id] = true
        else delete next[id]
      }
      return { selected: next }
    }),
  clearSelection: () => set({ selected: {} }),

  lint: {},
  setLint: (key, findings) =>
    set((state) => {
      const next = { ...state.lint }
      if (findings.length) next[key] = findings
      else delete next[key]
      return { lint: next }
    }),

  historyFor: null,
  openHistory: (historyFor) => set({ historyFor }),

  toasts: [],
  pushToast: (kind, text) =>
    set((state) => ({ toasts: [...state.toasts, { id: ++toastSeq, kind, text }] })),
  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}))
